export interface TenantScope {
  organizationId: string | null;
  activePartitionId: string | null;
  impersonationReason?: string | null;
  collaboratorCampaignIds?: string[] | null;
}

const ORGANIZATION_STORAGE_KEY = 'current_organization_id';
const ACTIVE_PARTITION_STORAGE_KEY = 'active_partition_id';
const IMPERSONATION_REASON_STORAGE_KEY = 'impersonation_reason';
const TENANT_SCOPE_EVENT = 'tenant-scope-changed';
export const TENANT_SCOPE_CHANGE_EVENT = TENANT_SCOPE_EVENT;

const PLACEHOLDER_SCOPE_IDS = new Set([
  '',
  'default',
  'default-org',
]);

const safeRead = (storage: Storage, key: string): string | null => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeWrite = (storage: Storage, key: string, value: string): void => {
  try {
    storage.setItem(key, value);
  } catch {
    // Best-effort only.
  }
};

const safeRemove = (storage: Storage, key: string): void => {
  try {
    storage.removeItem(key);
  } catch {
    // Best-effort only.
  }
};

const normalizeScopeId = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  const normalized = value.trim();
  if (!normalized || PLACEHOLDER_SCOPE_IDS.has(normalized)) {
    return null;
  }

  return normalized;
};

const normalizeImpersonationReason = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const canAccessStorage = (): boolean => typeof window !== 'undefined';
const notifyTenantScopeChanged = (): void => {
  if (!canAccessStorage()) {
    return;
  }
  window.dispatchEvent(new Event(TENANT_SCOPE_EVENT));
};

export const sanitizeTenantScopeId = (value: string | null | undefined): string | null =>
  normalizeScopeId(value);

export const getTenantScope = (): TenantScope => {
  if (!canAccessStorage()) {
    return {
      organizationId: null,
      activePartitionId: null,
      impersonationReason: null,
      collaboratorCampaignIds: null,
    };
  }

  const organizationId =
    normalizeScopeId(safeRead(window.localStorage, ORGANIZATION_STORAGE_KEY)) ??
    normalizeScopeId(safeRead(window.sessionStorage, ORGANIZATION_STORAGE_KEY));

  const activePartitionId =
    normalizeScopeId(safeRead(window.localStorage, ACTIVE_PARTITION_STORAGE_KEY)) ??
    normalizeScopeId(safeRead(window.sessionStorage, ACTIVE_PARTITION_STORAGE_KEY));
  const impersonationReason =
    normalizeImpersonationReason(safeRead(window.localStorage, IMPERSONATION_REASON_STORAGE_KEY)) ??
    normalizeImpersonationReason(safeRead(window.sessionStorage, IMPERSONATION_REASON_STORAGE_KEY));

  return {
    organizationId,
    activePartitionId,
    impersonationReason,
    collaboratorCampaignIds: null,
  };
};

export const setOrganizationScope = (organizationId: string | null | undefined): void => {
  if (!canAccessStorage()) {
    return;
  }

  const normalized = normalizeScopeId(organizationId);
  const storages = [window.localStorage, window.sessionStorage];

  for (const storage of storages) {
    if (normalized) {
      safeWrite(storage, ORGANIZATION_STORAGE_KEY, normalized);
    } else {
      safeRemove(storage, ORGANIZATION_STORAGE_KEY);
    }
  }
  notifyTenantScopeChanged();
};

export const setActivePartitionScope = (partitionId: string | null | undefined): void => {
  if (!canAccessStorage()) {
    return;
  }

  const normalized = normalizeScopeId(partitionId);
  const storages = [window.localStorage, window.sessionStorage];

  for (const storage of storages) {
    if (normalized) {
      safeWrite(storage, ACTIVE_PARTITION_STORAGE_KEY, normalized);
    } else {
      safeRemove(storage, ACTIVE_PARTITION_STORAGE_KEY);
    }
  }
  notifyTenantScopeChanged();
};

export const setTenantScope = (scope: Partial<TenantScope>): void => {
  if (Object.prototype.hasOwnProperty.call(scope, 'organizationId')) {
    setOrganizationScope(scope.organizationId ?? null);
  }

  if (Object.prototype.hasOwnProperty.call(scope, 'activePartitionId')) {
    setActivePartitionScope(scope.activePartitionId ?? null);
  }
  if (Object.prototype.hasOwnProperty.call(scope, 'impersonationReason')) {
    setImpersonationReason(scope.impersonationReason ?? null);
  }
};

export const setImpersonationReason = (reason: string | null | undefined): void => {
  if (!canAccessStorage()) {
    return;
  }

  const normalized = normalizeImpersonationReason(reason);
  const storages = [window.localStorage, window.sessionStorage];

  for (const storage of storages) {
    if (normalized) {
      safeWrite(storage, IMPERSONATION_REASON_STORAGE_KEY, normalized);
    } else {
      safeRemove(storage, IMPERSONATION_REASON_STORAGE_KEY);
    }
  }
  notifyTenantScopeChanged();
};

export const clearTenantScope = (): void => {
  setOrganizationScope(null);
  setActivePartitionScope(null);
  setImpersonationReason(null);
};
